import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LogOut, FileText, Clock, Download, BarChart2, Calendar, Image, Copy /* , Shrink */ } from 'lucide-react';
import { downloadFile } from '../utils/download';

function formatBytes(b) {
  if (!b) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  return (b / (1024 * 1024)).toFixed(1) + ' MB';
}

const STATUS_COLORS = {
  done: '#22c55e',
  error: '#ef4444',
  processing: '#f59e0b',
  pending: '#94a3b8'
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get('/api/auth/me', { headers }),
      axios.get('/api/history', { headers }),
      axios.get('/api/stats')
    ]).then(([u, h, s]) => {
      setUser(u.data);
      setHistory(h.data);
      setStats(s.data);
    }).catch(() => {
      localStorage.removeItem('token');
      navigate('/login');
    }).finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.8rem', fontWeight: 700 }}>
            Welcome, {user?.name || user?.email?.split('@')[0]} 
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>{user?.email} · {user?.plan?.toUpperCase()} Plan</p>
        </div>
        <button className="btn btn-outline" onClick={handleLogout}><LogOut size={16} /> Logout</button>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { icon: BarChart2, label: 'Total Conversions', value: stats.totalConversions || 0 },
          { icon: Calendar, label: 'Today', value: stats.todayConversions || 0 },
          { icon: Image, label: 'Images Converted', value: stats.breakdown?.imageConversions || 0 },
          { icon: Copy, label: 'PDFs Merged', value: stats.breakdown?.pdfMerge || 0 },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 16px', textAlign: 'center' }}>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><s.icon size={26} strokeWidth={1.75} /></div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.8rem', fontWeight: 700, color: 'var(--primary)' }}>{s.value}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, marginBottom: 32 }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.1rem', marginBottom: 16 }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Link to="/merge-pdf" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FileText size={14} /> Merge PDF</Link>
          <Link to="/image-convert" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Image size={14} /> Image Convert</Link>
          <Link to="/merge-pdf" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Copy size={14} /> Merge PDF</Link>
          {/* Compress PDF hidden while it doesn't shrink files yet.
          <Link to="/compress-pdf" className="btn btn-outline btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Shrink size={14} /> Compress PDF</Link> */}
        </div>
      </div>

      {/* Conversion History */}
      <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.1rem', marginBottom: 20 }}>
          <Clock size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Recent Conversions
        </h2>

        {history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            <FileText size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p>No conversions yet. <Link to="/" style={{ color: 'var(--primary)' }}>Start converting!</Link></p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {['File', 'From → To', 'Size', 'Status', 'Action'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map(conv => (
                  <tr key={conv._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px', maxWidth: 200 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>
                        {conv.inputFileName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {new Date(conv.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: '#E8F1FF', color: 'var(--primary)', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: '0.78rem' }}>
                        .{conv.inputFormat?.toUpperCase()}
                      </span>
                      {' → '}
                      <span style={{ background: '#f0fdf4', color: '#22c55e', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: '0.78rem' }}>
                        .{conv.outputFormat?.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-muted)' }}>
                      {formatBytes(conv.outputSize)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ background: STATUS_COLORS[conv.status] + '20', color: STATUS_COLORS[conv.status], padding: '3px 10px', borderRadius: 50, fontSize: '0.78rem', fontWeight: 600 }}>
                        {conv.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {conv.status === 'done' && conv.downloadUrl ? (
                        <button onClick={() => downloadFile(conv.downloadUrl, conv.downloadUrl.split('/').pop())} className="btn btn-sm btn-primary">
                          <Download size={12} /> Download
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
